import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useFacialAnalysis } from "./hooks/useFacialAnalysis";
import { FLOW_STEP_MAP, InterviewPhase, buildQueueForPhase } from "./mocks/flowSteps";
import { analyzeSentiment, speechToText } from "./services/googleCloudApi";
import {
  ACCOUNTABILITY_PROBE_ID,
  insertAdaptiveStep,
  shouldInsertAccountabilityProbe,
} from "./services/adaptiveEngine";
import {
  IntakeAnswer,
  IntakeFlowStep,
  TrackPreference,
} from "./types/interviewSession";
import { useVoiceRecorder } from "./hooks/useVoiceRecorder";
import { useIntakeCopy } from "./hooks/useIntakeCopy";
import { IntakeShell } from "./components/IntakeShell";
import { IntakeFooterNav } from "./components/IntakeFooterNav";
import { IntakeCameraPreview } from "./components/IntakeCameraPreview";

type Props = {
  stream: MediaStream | null;
  phase?: InterviewPhase;
  onComplete: (answers: IntakeAnswer[]) => void;
  onBackToCamera: () => void;
  onBackToWrapUp?: () => void;
};

function resolveStep(id: string): IntakeFlowStep {
  const step = FLOW_STEP_MAP[id];
  if (!step) {
    throw new Error(`Unknown flow step: ${id}`);
  }
  return step;
}

export function IntakeInterview({
  stream,
  phase = "main",
  onComplete,
  onBackToCamera,
  onBackToWrapUp,
}: Props) {
  const {
    flowText,
    progressLabel,
    skipLabel: formatSkipLabel,
    primaryLabel,
    trackOptionLabel,
    t,
  } = useIntakeCopy();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stepQueue, setStepQueue] = useState<string[]>(() =>
    buildQueueForPhase(phase)
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<IntakeAnswer[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<TrackPreference | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cameraHidden, setCameraHidden] = useState(false);
  const stepIndexRef = useRef(0);

  useEffect(() => {
    stepIndexRef.current = stepIndex;
  }, [stepIndex]);

  const currentStepId = stepQueue[stepIndex];
  const currentStep = useMemo(
    () => (currentStepId ? resolveStep(currentStepId) : null),
    [currentStepId]
  );

  const { isRecording, startRecording, stopRecording } = useVoiceRecorder(stream);

  const facialAnalysisEnabled =
    Boolean(stream) &&
    !isSubmitting &&
    currentStep?.recordsAudio === true;
  useFacialAnalysis(videoRef, facialAnalysisEnabled);

  const attachStreamToVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video || !stream || cameraHidden) return;
    video.srcObject = stream;
    void video.play().catch(() => {});
  }, [stream, cameraHidden]);

  useEffect(() => {
    attachStreamToVideo();
  }, [attachStreamToVideo, currentStepId, cameraHidden]);

  useEffect(() => {
    if (currentStep?.recordsAudio && !isSubmitting && !isRecording) {
      void startRecording();
    }
  }, [
    currentStep,
    isSubmitting,
    isRecording,
    startRecording,
    currentStepId,
  ]);

  useEffect(() => {
    if (currentStep?.type !== "thinking" || !currentStep.autoAdvanceMs) return;

    const timeout = window.setTimeout(() => {
      setStepIndex((prev) => prev + 1);
    }, currentStep.autoAdvanceMs);

    return () => window.clearTimeout(timeout);
  }, [currentStep, currentStepId]);

  const moveToNextStep = useCallback((skipped: boolean, step: IntakeFlowStep) => {
    const at = stepIndexRef.current;
    let nextQueue = stepQueue;

    if (skipped && step.skipBranch?.length) {
      nextQueue = [...stepQueue];
      nextQueue.splice(at + 1, 0, ...step.skipBranch);
      setStepQueue(nextQueue);
    }

    setStepIndex(at + 1);
  }, [stepQueue]);

  const finishStep = useCallback(
    async (skipped: boolean) => {
      if (!currentStep || isSubmitting) return;

      if (currentStep.type === "track-choice") {
        if (!selectedTrack && !skipped) return;
        const answer: IntakeAnswer = {
          stepId: currentStep.id,
          transcript: "",
          sentiment: { score: 0, magnitude: 0 },
          duration: 0,
          skipped,
          trackPreference: selectedTrack ?? undefined,
        };
        setAnswers((prev) => [...prev, answer]);
        moveToNextStep(skipped, currentStep);
        return;
      }

      if (currentStep.type === "thinking") {
        setStepIndex((prev) => prev + 1);
        return;
      }

      setIsSubmitting(true);

      try {
        let transcript = "";
        let sentiment = { score: 0, magnitude: 0 };
        let duration = 0;

        if (currentStep.recordsAudio) {
          const recording = await stopRecording();
          if (!skipped && recording?.base64) {
            duration = recording.durationMs;
            transcript = await speechToText(recording.base64, recording.mimeType);
            if (transcript) {
              sentiment = await analyzeSentiment(transcript);
            }
          }
        }

        const answer: IntakeAnswer = {
          stepId: currentStep.id,
          transcript,
          sentiment,
          duration,
          skipped,
        };
        const nextAnswers = [...answers, answer];
        setAnswers(nextAnswers);

        const isLast = stepIndex >= stepQueue.length - 1;
        if (isLast) {
          onComplete(nextAnswers);
          return;
        }

        let updatedQueue = stepQueue;
        if (
          shouldInsertAccountabilityProbe(currentStep.id, skipped, sentiment.score)
        ) {
          updatedQueue = insertAdaptiveStep(
            stepQueue,
            stepIndexRef.current,
            ACCOUNTABILITY_PROBE_ID
          );
          if (updatedQueue.length > stepQueue.length) {
            setStepQueue(updatedQueue);
            setStepIndex(stepIndexRef.current + 1);
            return;
          }
        }

        moveToNextStep(skipped, currentStep);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      answers,
      currentStep,
      isSubmitting,
      moveToNextStep,
      onComplete,
      selectedTrack,
      stepIndex,
      stepQueue.length,
      stopRecording,
    ]
  );

  const handleBack = () => {
    if (stepIndex === 0) {
      if (phase === "deeper" && onBackToWrapUp) {
        onBackToWrapUp();
      } else {
        onBackToCamera();
      }
      return;
    }
    if (isSubmitting || currentStep?.type === "thinking") return;
    setAnswers((prev) => prev.slice(0, -1));
    setStepIndex((prev) => Math.max(0, prev - 1));
  };

  const skipLabel =
    currentStep?.skipIndex != null && currentStep.skipTotal != null
      ? formatSkipLabel(currentStep.skipIndex, currentStep.skipTotal)
      : undefined;

  const stepCopy = currentStepId
    ? flowText(currentStepId)
    : { headline: "", body: "" };

  const showCamera =
    !cameraHidden &&
    currentStep &&
    (currentStep.recordsAudio ||
      currentStep.type === "intro" ||
      currentStep.type === "question" ||
      currentStep.type === "track-choice" ||
      currentStep.type === "thinking");

  if (!currentStep) {
    return null;
  }

  return (
    <IntakeShell
      progressStep={3}
      progressTotal={5}
      progressLabel={progressLabel(3, 5)}
      footer={
        currentStep.type === "thinking" ? undefined : (
          <IntakeFooterNav
            onBack={handleBack}
            backLabel={t("intake.back")}
            backDisabled={isSubmitting}
            skipLabel={skipLabel}
            onSkip={skipLabel ? () => void finishStep(true) : undefined}
            skipDisabled={isSubmitting}
            primaryLabel={primaryLabel(currentStep)}
            onPrimary={() => void finishStep(false)}
            primaryDisabled={
              isSubmitting ||
              (currentStep.type === "track-choice" && !selectedTrack)
            }
          />
        )
      }
    >
      <h1 className="intake-flow__headline">{stepCopy.headline}</h1>
      <p className="intake-flow__subtext intake-flow__subtext--center">
        {stepCopy.body}
      </p>

      {currentStep.type === "track-choice" && currentStep.options && (
        <div className="intake-track-choice">
          {currentStep.options.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`intake-track-choice__card${
                selectedTrack === option.id
                  ? " intake-track-choice__card--selected"
                  : ""
              }`}
              onClick={() => setSelectedTrack(option.id)}
            >
              <span className="intake-track-choice__icon" aria-hidden="true">
                {option.id === "personal" ? "👤" : "💼"}
              </span>
              <span>{trackOptionLabel(option.id)}</span>
            </button>
          ))}
        </div>
      )}

      {showCamera && (
        <IntakeCameraPreview
          videoRef={videoRef}
          stream={stream}
          showRec={
            isRecording &&
            (currentStep.recordsAudio === true || currentStep.type === "intro")
          }
          onClose={() => setCameraHidden(true)}
        />
      )}
    </IntakeShell>
  );
}
